import { Article } from '../../components/Layout'
import { H1, H2, P, Lead, UL, LI, Callout, Code, PropTable, Steps, Step, Screenshot, UIMockup } from '../../components/DocComponents'

const toc = [
  { id: 'layout', title: 'Layout model', level: 2 as const },
  { id: 'create', title: 'Adding sections', level: 2 as const },
  { id: 'buttons', title: 'Section buttons', level: 2 as const },
  { id: 'fields', title: 'Section fields', level: 2 as const },
]

export function Sections() {
  return (
    <Article toc={toc}>
      <H1>Sections & Layout</H1>
      <Lead>Sections divide a Plate into vertical columns. Each section holds one or more Views stacked vertically inside it.</Lead>

      <H2 id="layout">Layout model</H2>
      <P>The Plate layout is a <strong>flex row</strong>. Each Section has a percentage width (e.g. 50%, 33%, 100%). Sections are sorted by their <Code>rank</Code> value — lower ranks appear first (left).</P>
      <UIMockup label="Example: two-column layout (60% + 40%)">
        <div className="flex gap-0 text-xs text-slate-500 font-mono">
          <div className="flex-none w-[60%] bg-blue-50 border-r border-slate-200 p-3 text-center">Section A — 60%</div>
          <div className="flex-none w-[40%] bg-slate-50 p-3 text-center">Section B — 40%</div>
        </div>
      </UIMockup>
      <Callout type="tip">For a single full-width layout, use one Section at 100%. For a three-column layout, use three Sections at 33% each.</Callout>

      <H2 id="create">Adding sections</H2>
      <Steps>
        <Step n={1} title="Open the Builder tab">On the plate detail page, click the <strong>Builder</strong> tab.</Step>
        <Step n={2} title="Click Add Section">Fill in an optional title, set the width percentage, and confirm.</Step>
        <Step n={3} title="Add Views inside">Click <strong>Add View</strong> inside the section to add data widgets.</Step>
      </Steps>
      <Screenshot alt="Builder tab showing two sections side by side, each containing one or more views" caption="Sections and their views appear exactly as they will in the Plate Runner." />

      <H2 id="buttons">Section buttons</H2>
      <P>Each section can have one or more <strong>buttons</strong> displayed in its header. These are navigation buttons that take the user to another plate, forwarding the current input parameters as URL query params.</P>
      <UL>
        <LI>Buttons appear in the top-right of the section header (next to the title)</LI>
        <LI>Each button links to a <Code>destinationPlateId</Code></LI>
        <LI>All current <Code>runInputs</Code> are forwarded automatically as query params</LI>
        <LI>Button appearance is fully customisable: background colour, text colour, border, border-radius</LI>
      </UL>
      <Callout type="info">Section buttons are the recommended way to navigate between plates while preserving context — e.g. a "Add Article" button on a "Preorder Overview" plate forwards the <Code>preorderId</Code> to the "Add Article" plate so the new record is automatically linked.</Callout>

      <H2 id="fields">Section fields</H2>
      <PropTable props={[
        { name: 'title', type: 'string', description: 'Optional heading displayed above the section\'s views.' },
        { name: 'width', type: 'number', required: true, description: 'Percentage of the plate width this section occupies (1–100).' },
        { name: 'rank', type: 'number', default: '0', description: 'Sort order. Lower = further left.' },
      ]} />
    </Article>
  )
}
